<template>
  <iColumn
    gap="small"
    width="fill"
    :style-overrides="PADDING"
    data-test="comment-input"
  >
    <iRow
      v-if="canComment && reactionsOpen"
      align="between"
      width="fill"
      wrap="nowrap"
      data-test="story-comments-quick-reactions"
    >
      <iButton
        v-for="emoji in QUICK_REACTION_EMOJI"
        :key="emoji"
        variant="text"
        :style-overrides="REACTION"
        :aria-label="`Add ${emoji}`"
        data-test="story-comments-quick-reaction"
        @mousedown.prevent
        @click="addReaction(emoji)"
      >
        <iText font-size="large">
          {{ emoji }}
        </iText>
      </iButton>
    </iRow>
    <iRow
      v-if="replyTo"
      align="between"
      vertical-align="middle"
      width="fill"
      wrap="nowrap"
      data-test="story-comments-replying-to"
    >
      <iText variant="subtle" font-size="small">
        Replying to {{ replyTo.answering.authorName || "this comment" }}
      </iText>
      <iButton
        variant="text"
        size="small"
        icon-left="material-symbols:close-rounded"
        aria-label="Stop replying"
        data-test="story-comments-stop-replying"
        @click="$emit('stop-reply')"
      />
    </iRow>
    <StoryCommentBox
      ref="box"
      :story-id="storyId"
      layout="inline"
      :placeholder="replyTo ? 'Add a reply…' : 'Add a comment…'"
      submit-label="Post"
      :initial-segments="initialSegments"
      :reply-to="replyTo ? replyTo.parent : null"
      :input-style-overrides="INPUT_STYLE"
      @replied="$emit('stop-reply')"
    >
      <template #leading>
        <StoryCommentEmojiButton ref="emoji" :open="reactionsOpen" @toggle="reactionsOpen = !reactionsOpen" />
      </template>
    </StoryCommentBox>
  </iColumn>
</template>

<script>
import { mapState } from "pinia";
import { useConfigurationStore } from "%/dynamic-component-editor/stores/configuration-store";
import { useStoryCommentsStore } from "%/dynamic-component-editor/stores/story-comments-store";
import StoryCommentBox from "%/dynamic-component-editor/components/story-comments/StoryCommentBox.vue";
import StoryCommentEmojiButton from "%/dynamic-component-editor/components/story-comments/StoryCommentEmojiButton.vue";
import { QUICK_REACTION_EMOJI } from "%/dynamic-component-editor/helpers/story-comments.mjs";

const PADDING = Object.freeze({ paddingTop: "small", paddingBottom: "small", paddingLeft: "standard", paddingRight: "large" });
const INPUT_STYLE = Object.freeze({
  mentionColor: "branded",
  backgroundColor: "none",
  borderColor: "none",
  focusBorderColor: "none",
});
const REACTION = Object.freeze({
  standardPaddingLeft: "extraSmall",
  standardPaddingRight: "extraSmall",
});

export default {
  name: "StoryCommentsOverlayComposer",
  components: { StoryCommentBox, StoryCommentEmojiButton },
  props: {
    storyId: {
      type: Number,
      required: true,
    },
    replyTo: {
      type: Object,
      default: null,
    },
    initialSegments: {
      type: Array,
      default: null,
    },
  },
  emits: ["stop-reply"],
  data() {
    return {
      reactionsOpen: false,
      QUICK_REACTION_EMOJI,
      INPUT_STYLE,
      PADDING,
      REACTION,
    };
  },
  computed: {
    ...mapState(useConfigurationStore, ["canCommentOnPosts"]),
    canComment() {
      return this.canCommentOnPosts && useStoryCommentsStore().isReaderSignedIn;
    },
  },
  methods: {
    addReaction(emoji) {
      this.$refs.emoji?.insert(emoji);
    },
    focus() {
      this.$refs.box?.focus();
    },
    payload() {
      return this.$refs.box?.payload() ?? null;
    },
  },
};
</script>
